import React, { useState } from 'react';
import { AlertTriangle, CalendarClock, Clock, Info, Warehouse } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { fmtDate } from '@/features/dashboard/data/claudeMockData';
import type { CostCategory, CostTariffs, LocaleTariffPeriod, TerminalTariffPeriod } from '../interfaces/costs.interface';
import { categoryLabel, fmtMoney, tierRange, validityLabel } from '../utils/costs';

type Lang = 'ro' | 'en';

interface TariffsPanelProps {
  tariffs?: CostTariffs;
  isLoading: boolean;
  error: Error | null;
}

const TERMINAL_CATEGORIES: CostCategory[] = ['standard', 'imo', 'reefer', 'special'];

const price = (v: number | null, lang: Lang) => (v == null ? '—' : fmtMoney(v, lang));

function LocaleTable({ period, lang }: { period: LocaleTariffPeriod; lang: Lang }) {
  const isRo = lang === 'ro';
  const has45 = period.rows.some((r) => r.price45 != null);
  return (
    <div className="table-wrap">
      <table className="data compact cst-tariff">
        <thead>
          <tr>
            <th>{isRo ? 'Tip container' : 'Container type'}</th>
            <th className="text-right">20'</th>
            <th className="text-right">40'</th>
            {has45 && <th className="text-right">45'</th>}
          </tr>
        </thead>
        <tbody>
          {period.rows.map((r) => (
            <tr key={r.category}>
              <td>
                <div className="fw-500">{categoryLabel(r.category, lang)}</div>
                {r.inherited && <div className="muted fs-11">{isRo ? 'același tarif ca standard' : 'same as standard'}</div>}
              </td>
              <td className="text-right mono">{price(r.price20, lang)}</td>
              <td className="text-right mono">{price(r.price40, lang)}</td>
              {has45 && <td className="text-right mono">{price(r.price45, lang)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TerminalCard({
  period,
  category,
  kind,
  lang,
}: {
  period: TerminalTariffPeriod | null;
  category: CostCategory;
  kind: 'demurrage' | 'storage';
  lang: Lang;
}) {
  const isRo = lang === 'ro';
  const isDem = kind === 'demurrage';
  const Icon = isDem ? Clock : Warehouse;
  const slice =
    period?.categories.find((c) => c.category === category) ?? period?.categories.find((c) => c.category === 'standard');
  const inherited = !!slice && slice.category !== category;

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <div className="title cst-title">
            <Icon size={15} />
            {isDem ? (isRo ? 'Contrastalii (demurrage)' : 'Demurrage') : isRo ? 'Depozitare în terminal' : 'Terminal storage'}
          </div>
          <div className="sub">
            {isDem
              ? isRo
                ? 'Folosirea containerului liniei, de la descărcare până la returnarea lui gol'
                : "Use of the carrier's container, from discharge until it is returned empty"
              : isRo
                ? `Staționarea în terminalul ${period?.terminal || 'SOCEP'}, de la descărcare până la ieșirea din terminal`
                : `Stay in the ${period?.terminal || 'SOCEP'} terminal, from discharge until gate-out`}
          </div>
        </div>
      </div>
      {!period || !slice ? (
        <div className="empty">
          <div className="fw-600">{isRo ? 'Tarif indisponibil momentan' : 'Tariff currently unavailable'}</div>
        </div>
      ) : (
        <>
          <div className="cst-free-hero">
            <span className="n">{slice.freeDays}</span>
            <span className="l">
              {isRo ? (slice.freeDays === 1 ? 'zi liberă' : 'zile libere') : slice.freeDays === 1 ? 'free day' : 'free days'}
              <span className="muted">
                {isRo ? ' · inclusiv ziua descărcării' : ' · including the discharge day'}
                {inherited && (isRo ? ' · ca la standard' : ' · as standard')}
              </span>
            </span>
          </div>
          <div className="table-wrap">
            <table className="data compact cst-tariff">
              <thead>
                <tr>
                  <th>{isRo ? 'Perioadă' : 'Period'}</th>
                  <th className="text-right">20'</th>
                  <th className="text-right">40'</th>
                  <th className="text-right">45'</th>
                </tr>
              </thead>
              <tbody>
                {slice.freeDays > 0 && (
                  <tr className="cst-free-row">
                    <td>{tierRange({ fromDay: 1, toDay: slice.freeDays }, lang)}</td>
                    <td colSpan={3} className="text-right">
                      {isRo ? 'gratuit' : 'free'}
                    </td>
                  </tr>
                )}
                {slice.tiers.map((t) => (
                  <tr key={t.fromDay}>
                    <td>{tierRange(t, lang)}</td>
                    <td className="text-right mono">{fmtMoney(t.price20, lang)}</td>
                    <td className="text-right mono">{fmtMoney(t.price40, lang)}</td>
                    <td className="text-right mono">{fmtMoney(t.price45, lang)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cst-foot muted fs-12">
            {period.currency} / {isRo ? 'container / zi, fără TVA' : 'container / day, excl. VAT'} ·{' '}
            {validityLabel(period.validFrom, period.validTo, lang, (iso) => fmtDate(iso, lang))}
          </div>
        </>
      )}
    </div>
  );
}

const TariffsPanel: React.FC<TariffsPanelProps> = ({ tariffs, isLoading, error }) => {
  const { currentLanguage: lang } = useLanguage();
  const isRo = lang === 'ro';
  const [category, setCategory] = useState<CostCategory>('standard');

  if (error) {
    return (
      <div className="card" style={{ padding: 0 }}>
        <div className="empty">
          <div className="icon">
            <AlertTriangle size={22} />
          </div>
          <div className="fw-600">{isRo ? 'Tarifele nu au putut fi încărcate' : 'The tariffs could not be loaded'}</div>
        </div>
      </div>
    );
  }
  if (isLoading || !tariffs) {
    return (
      <div className="card" style={{ padding: 0 }}>
        <div className="empty">
          <div className="fw-600">{isRo ? 'Se încarcă tarifele…' : 'Loading tariffs…'}</div>
        </div>
      </div>
    );
  }

  const locale = tariffs.locale;
  const next = tariffs.localeNext;
  const fmt = (iso: string) => fmtDate(iso, lang);

  return (
    <>
      <div className="cst-tariff-grid">
        <div className="card">
          <div className="card-header">
            <div>
              <div className="title">{isRo ? 'Cheltuieli locale (THC)' : 'Local charges (THC)'}</div>
              <div className="sub">
                {isRo
                  ? 'Manipularea containerului plin și gol în terminal, eliberarea, expediția și documentația'
                  : 'Full and empty container handling in the terminal, release, dispatch and documentation'}
              </div>
            </div>
          </div>
          {locale ? (
            <>
              <LocaleTable period={locale} lang={lang} />
              <div className="cst-foot muted fs-12">
                {locale.currency} / container, {isRo ? 'fără TVA' : 'excl. VAT'} · {validityLabel(locale.validFrom, locale.validTo, lang, fmt)}
              </div>
            </>
          ) : (
            <div className="empty">
              <div className="fw-600">{isRo ? 'Tarif indisponibil momentan' : 'Tariff currently unavailable'}</div>
            </div>
          )}
          {next && (
            <div className="cst-next">
              <CalendarClock size={15} />
              <div>
                <div className="fw-600">
                  {isRo ? 'Tarif nou' : 'New tariff'} · {validityLabel(next.validFrom, next.validTo, lang, fmt)}
                </div>
                <div className="cst-next-prices mono">
                  {next.rows
                    .filter((r) => r.category === 'standard')
                    .map((r) =>
                      [
                        r.price20 != null && `20' ${fmtMoney(r.price20, lang)}`,
                        r.price40 != null && `40' ${fmtMoney(r.price40, lang)}`,
                        r.price45 != null && `45' ${fmtMoney(r.price45, lang)}`,
                      ]
                        .filter(Boolean)
                        .join(' · '),
                    )}{' '}
                  {next.currency} ({isRo ? 'standard' : 'standard'})
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="cst-side">
          <div className="card card-pad cst-how">
            <div className="fw-600 mb-2">{isRo ? 'Cum se calculează' : 'How it is calculated'}</div>
            <ul>
              {isRo ? (
                <>
                  <li>Categoria vine din tipul containerului; marfa periculoasă (IMO) are tarif propriu.</li>
                  <li>Zilele se numără calendaristic, iar <b>ziua descărcării este ziua 1</b>.</li>
                  <li>
                    <b>Contrastaliile</b> curg până la returnarea containerului gol; <b>depozitarea</b> până la ieșirea din terminal.
                  </li>
                  <li>După zilele libere se plătește prețul pe zi al intervalului în care se află fiecare zi.</li>
                  <li>Facturarea se face în lei, la cursul din ziua facturii.</li>
                </>
              ) : (
                <>
                  <li>The category follows the container type; dangerous goods (IMO) have their own tariff.</li>
                  <li>
                    Days are calendar days and <b>the discharge day is day 1</b>.
                  </li>
                  <li>
                    <b>Demurrage</b> runs until the empty container is returned; <b>storage</b> until it leaves the terminal.
                  </li>
                  <li>After the free days, each day is charged at the rate of the period it falls in.</li>
                  <li>Invoices are issued in RON at the exchange rate on the invoice date.</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>

      {(tariffs.demurrage || tariffs.storage) && (
        <>
          <div className="cst-section-head">
            <div>
              <div className="cst-section-title">{isRo ? 'Contrastalii și depozitare' : 'Demurrage and storage'}</div>
              <div className="muted fs-12">
                {isRo ? 'Alegeți tipul containerului pentru zilele libere și prețurile pe zi' : 'Pick the container type to see free days and daily rates'}
              </div>
            </div>
            <div className="ctn-chips cst-cat-chips" role="tablist" aria-label={isRo ? 'Tip container' : 'Container type'}>
              {TERMINAL_CATEGORIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  role="tab"
                  aria-selected={category === c}
                  className={'ctn-chip cst-tab' + (category === c ? ' active' : '')}
                  onClick={() => setCategory(c)}
                >
                  {categoryLabel(c, lang).replace(/ \(.*\)$/, '')}
                </button>
              ))}
            </div>
          </div>
          <div className="cst-terminal-grid">
            <TerminalCard period={tariffs.demurrage} category={category} kind="demurrage" lang={lang} />
            <TerminalCard period={tariffs.storage} category={category} kind="storage" lang={lang} />
          </div>
        </>
      )}

      <div className="cst-note warn">
        <Info size={15} />
        <div>
          {isRo
            ? 'Acestea sunt tarifele standard. Dacă oferta dumneavoastră conține prețuri sau zile libere negociate, se aplică cele din ofertă.'
            : 'These are the standard tariffs. If your offer includes negotiated prices or free days, those apply instead.'}
        </div>
      </div>
    </>
  );
};

export default TariffsPanel;
